/*
 * See the file LICENSE for the full license governing this code.
 */

import * as React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import Link from '@mui/material/Link';
import Loader from '../Loader';
import {
  createAxiosInstance,
  networkWithErrorConvert,
  queryClient,
} from '../../api';
import { useMutation } from 'react-query';

const AuthContext = React.createContext();
const SIGN_IN_FN = 'signin';
const SIGN_UP_FN = 'signup';
const VERIFY_EMAIL_FN = 'verifyemail';
const AGREE_TERMS_FN = 'agreeTerms';

const submit =
  (...keys) =>
  (authType) =>
  (props = { disableOnSuccess: false }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const user = useAuth();
    const from = location.state?.from?.pathname || '/';
    return useMutation(
      (event) => {
        const data = new FormData(event.target);
        return user[authType](
          Object.assign({}, ...keys.map((key) => ({ [key]: data.get(key) })))
        );
      },
      props.disableOnSuccess
        ? undefined
        : {
            onSuccess: (...args) => {
              navigate(from, { replace: true });
              props.onSuccess?.(...args);
            },
          }
    );
  };

const resendOtpWithoutErrorHandling = async () => {
  try {
    await networkWithErrorConvert({
      url: '/v1/user/resend-otp',
      method: 'post',
    });
  } catch {}
};

const submitAuth = submit('username', 'password');
export const submitSignIn = () => {
  return submitAuth(SIGN_IN_FN)({
    onSuccess: async (response) => {
      if (response && !response.verified) {
        await resendOtpWithoutErrorHandling();
      }
    },
  });
};
export const submitSignUp = submitAuth(SIGN_UP_FN);
export const submitVerification = submit('otp')(VERIFY_EMAIL_FN);
export const submitAgreeTerms = submit()(AGREE_TERMS_FN);

export function LinkToAnotherAuth({ children, authLink }) {
  const navigate = useNavigate();
  return (
    <Link
      style={{ cursor: 'pointer' }}
      onClick={() => navigate(authLink, { replace: true })}
      variant="body2"
    >
      {children}
    </Link>
  );
}

export function useAuth() {
  return React.useContext(AuthContext);
}

const initState = () => ({
  authenticated: false,
  signedByPassword: false,
  initialized: false,
  verified: false,
  agreedOnTerms: false,
  permissions: 'free',
});

async function setXsrfToken() {
  const response =
    (await networkWithErrorConvert({ url: '/v1/xsrf-token' })) || {};
  createAxiosInstance({
    headers: { 'XSRF-token': response['xsrf-token'] },
  });
}

export function AuthProvider({ children }) {
  let [userInfo, setUser] = React.useState(initState());

  React.useEffect(() => {
    const fetchUserInfo = async () => {
      try {
        await setXsrfToken();
        const { verified, permissions, email, agreedOnTerms } =
          (await networkWithErrorConvert({ url: '/v1/user' })) || {};
        if (email && !verified) {
          resendOtpWithoutErrorHandling();
        }
        setUser((prevObj) => ({
          ...prevObj,
          authenticated: true,
          initialized: true,
          username: email,
          verified,
          agreedOnTerms,
          permissions,
        }));
      } catch (err) {
        setUser({ ...initState(), authenticated: false, initialized: true });
      }
    };
    fetchUserInfo();
  }, []);

  const authQuery = (url) => async (auth) => {
    const res = await networkWithErrorConvert({
      method: 'post',
      url,
      auth,
    });
    const { verified, email: username, permissions, agreedOnTerms } = res;
    setUser({
      signedByPassword: true,
      authenticated: true,
      initialized: true,
      verified,
      username,
      agreedOnTerms,
      permissions,
    });
    return res;
  };

  const expireUser = () => {
    setUser({ ...initState(), initialized: true });
    queryClient.clear();
  };

  const resetUser = (url) => async () => {
    await networkWithErrorConvert({ url, method: 'delete' });
    expireUser();
  };

  const signout = resetUser('/v1/signout');
  const deleteUser = resetUser('/v1/user');

  const verifyEmail = async (data) => {
    const response = await networkWithErrorConvert({
      method: 'post',
      url: '/v1/user/verify-email',
      data,
    });
    setUser((prevState) => ({ ...prevState, verified: response.verified }));
  };

  const agreeOnTerms = async () => {
    const response = await networkWithErrorConvert({
      method: 'post',
      url: '/v1/user/agree-terms',
    });
    setUser((prevState) => ({
      ...prevState,
      agreedOnTerms: response.agreedOnTerms,
    }));
  };

  const setUsername = (name) => {
    setUser((prevState) => ({ ...prevState, username: name }));
  };

  const value = {
    ...userInfo,
    [VERIFY_EMAIL_FN]: verifyEmail,
    [SIGN_IN_FN]: authQuery('/v1/signin'),
    [SIGN_UP_FN]: authQuery('/v1/signup'),
    [AGREE_TERMS_FN]: agreeOnTerms,
    signout,
    expireUser,
    deleteUser,
    setUsername,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function RequireAuth({ children }) {
  const auth = useAuth();
  const location = useLocation();

  if (!auth.initialized) {
    return <Loader />;
  }
  if (!auth.authenticated) {
    return <Navigate to="/signin" state={{ from: location }} replace />;
  }
  if (!auth.verified) {
    return <Navigate to="/verify-email" state={{ from: location }} replace />;
  }
  if (!auth.agreedOnTerms) {
    return (
      <Navigate to="/terms-of-service" state={{ from: location }} replace />
    );
  }

  return children;
}
